import type { DefaultOperatorName, Field, RuleGroupType } from 'react-querybuilder';
import { uniq } from 'ramda';
import { ElectricityMeasurementCategory, GasMeasurementCategory, Commodity } from '@enmon/commons';
import { ElectricityDeviceCategory, GasDeviceCategory, OtherDeviceCategory } from '@enmon/commons/lib/shared/meter';
import { OutputTechnology, type SerializedThirdParty } from '@enmon/commons/lib/tools';
import { MeterOwnerType, type SerializedMeter } from '@enmon/commons/lib/enmon';
import type { ConditionFieldName } from './const';
import {
  SHARED_ENUM_FIELD_CONFIG,
  SHARED_NUMBER_FIELD_CONFIG,
  SHARED_TEXT_FIELD_CONFIG,
  enumToOptions,
} from './shared-field-config';

/**
 * Utility function to check if the rule group contains UtilityCompany owner type
 */
export const containsOwnerTypeUtility = (ruleGroup?: RuleGroupType): boolean => {
  if (!ruleGroup?.rules) {
    return false;
  }

  return ruleGroup?.rules.some(rule => {
    if ('rules' in rule) {
      return containsOwnerTypeUtility(rule); // recurse into nested group
    }

    // simple '=' check for single rule... complex check for same logical condition are not evaluated e.g. 'not in' or 'in'
    return (
      'field' in rule &&
      rule.field === ('ownerType' satisfies keyof SerializedMeter) &&
      rule.operator === '=' &&
      // when UtilityCompany is selected and operator changes from 'in' to '=' value stays as array
      (rule.value === MeterOwnerType.UtilityCompany || rule.value?.includes(MeterOwnerType.UtilityCompany))
    );
  });
};

/**
 * Creates the Commodity field
 */
export const createCommodityField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'commodity', // TODO: !WARNING! CHECK POSSIBLY BREAKING CHANGE
  label: 'Type',
  values: enumToOptions(Commodity),
  ...SHARED_ENUM_FIELD_CONFIG,
});

/**
 * Creates the Owner Type field
 */
export const createOwnerTypeField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'ownerType',
  label: 'Owner Type',
  values: enumToOptions(MeterOwnerType),
  ...SHARED_ENUM_FIELD_CONFIG,
});

/**
 * Creates the Owner field with dynamic behavior based on owner type selection
 * @param containsUtilityOwnerType - Whether the UtilityCompany owner type is selected
 * @param thirdParties - Optional list of third parties for dropdown when utility company is selected
 */
export const createOwnerField = (
  containsUtilityOwnerType: boolean,
  thirdParties?: Array<Partial<Pick<SerializedThirdParty, 'id' | 'name'>>>,
): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'owner',
  label: 'Owner',
  ...(containsUtilityOwnerType
    ? {
        values: thirdParties?.map(({ name }) => ({ name: name ?? '', label: name ?? '' })),
        ...SHARED_ENUM_FIELD_CONFIG,
      }
    : {
        ...SHARED_TEXT_FIELD_CONFIG,
      }),
});

/**
 * Creates the LoRa RSSI field
 */
export const createLorawanRssiField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'lorawan_rssi',
  label: 'LoRa RSSI',
  ...SHARED_NUMBER_FIELD_CONFIG,
});

/**
 * Creates the NB-IoT RSSI field
 */
export const createNbiotRssiField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'nbiot_rssi',
  label: 'NB-IoT RSSI',
  ...SHARED_NUMBER_FIELD_CONFIG,
});

/**
 * Creates the Measurement Category field
 */
export const createMeasurementCategoryField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'measurementCategory',
  label: 'Measurement category',
  values: enumToOptions(
    Object.fromEntries(
      uniq([Object.values(ElectricityMeasurementCategory), Object.values(GasMeasurementCategory)].flat()).map(val => [
        val,
        val,
      ]),
    ),
  ),
  ...SHARED_ENUM_FIELD_CONFIG,
});

/**
 * Creates the Device Category field
 */
export const createDeviceCategoryField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'deviceCategory',
  label: 'Device category',
  values: [
    enumToOptions(OtherDeviceCategory),
    enumToOptions(ElectricityDeviceCategory),
    enumToOptions(GasDeviceCategory),
  ].flat(),
  ...SHARED_ENUM_FIELD_CONFIG,
});

/**
 * Creates the Meter Mount Technology field
 */
export const createMeterMountTechnologyField = (): Field<ConditionFieldName, DefaultOperatorName> => ({
  name: 'meter_mount_technology',
  label: 'Meter mount technology',
  values: enumToOptions(OutputTechnology),
  ...SHARED_ENUM_FIELD_CONFIG,
});
